import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import './AdminPage.css';

import { API_BASE as API } from '../../apiConfig';

const PEOPLE_CATEGORIES = [
    'Professor In-charge', 'Project Staff', 'PhD',
    'M.Tech (By Research)', 'M.Tech', 'B.Tech', 'Interns', 'Alumni', 'Past Contributors'
];

const PROJECT_CATEGORIES = [
    'Funded/Sponsored Projects (Ongoing)',
    'Funded/Sponsored Projects (Completed)',
    'Consultancy Work (Ongoing)',
    'Consultancy Work (Completed)',
];

const PUBLICATION_CATEGORIES = [
    'Book Chapters', 'Journal Publications', 'Conference Publications', 'Patents'
];

const CAN_MOVE_TO_ALUMNI = ['Project Staff', 'PhD', 'M.Tech (By Research)', 'M.Tech', 'B.Tech'];

// ══════════════════════════════════════════════════════════════════════════════
//  PERSON MODAL (ADD & EDIT)
// ══════════════════════════════════════════════════════════════════════════════
function PersonModal({ defaultCategory, personToEdit, onClose, onSaved }) {
    const isEdit = !!personToEdit;
    const [form, setForm] = useState({
        category: personToEdit?.category || defaultCategory || PEOPLE_CATEGORIES[0],
        name: personToEdit?.name || '', 
        designation: personToEdit?.designation || '', 
        researchInterests: personToEdit?.researchInterests || '',
        projectInvolved: personToEdit?.projectInvolved || '', 
        image: personToEdit?.image || '', 
        year: personToEdit?.year || ''
    });
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const isPastContributor = form.category === 'Past Contributors';

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        setError('');
        const formData = new FormData();
        formData.append('image', file);

        try {
            const res = await fetch(`${API}/upload`, {
                method: 'POST',
                body: formData
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Upload failed');
            set('image', data.url);
        } catch (err) {
            setError('Failed to upload image: ' + err.message);
        } finally {
            setUploading(false);
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) return setError('Name is required.');
        setSaving(true); setError('');
        try {
            const url = isEdit ? `${API}/people/${personToEdit._id}` : `${API}/people`;
            const method = isEdit ? 'PATCH' : 'POST';
            const res = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(form)
            });
            if (!res.ok) throw new Error((await res.json()).error || 'Failed');
            onSaved();
        } catch (err) {
            setError(err.message);
        } finally { setSaving(false); }
    };

    return (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
            <div className="modal">
                <div className="modal-header">
                    <h2>{isEdit ? 'Edit Person' : 'Add Person'}</h2>
                    <button className="modal-close" onClick={onClose}>✕</button>
                </div>
                <form onSubmit={submit}>
                    <div className="form-grid">
                        <div className="form-group full-width">
                            <label>Category<span className="required-star">*</span></label>
                            <select value={form.category} onChange={e => set('category', e.target.value)}>
                                {PEOPLE_CATEGORIES.map(c => <option key={c}>{c}</option>)}
                            </select>
                        </div>
                        <div className="form-group full-width">
                            <label>Name<span className="required-star">*</span></label>
                            <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Full name" />
                        </div>
                        
                        {!isPastContributor && (
                            <div className="form-group full-width">
                                <label>Designation</label>
                                <input value={form.designation} onChange={e => set('designation', e.target.value)}
                                    placeholder="e.g. M.Tech, Ph.D. Scholar, Research Assistant" />
                            </div>
                        )}

                        {!isPastContributor && (<>
                            <div className="form-group full-width">
                                <label>Research Interests</label>
                                <input value={form.researchInterests} onChange={e => set('researchInterests', e.target.value)}
                                    placeholder="e.g. Cybersecurity, IoT, Machine Learning" />
                            </div>
                            <div className="form-group full-width">
                                <label>Project Involved</label>
                                <input value={form.projectInvolved} onChange={e => set('projectInvolved', e.target.value)}
                                    placeholder="Project name or description" />
                            </div>
                            <div className="form-group full-width">
                                <label>Photo Upload</label>
                                <input type="file" accept="image/*" onChange={handleFileUpload} />
                                {uploading && <span className="upload-loader">Uploading...</span>}
                            </div>
                            <div className="form-group full-width">
                                <label>Photo URL (or enter manually)</label>
                                <input value={form.image} onChange={e => set('image', e.target.value)}
                                    placeholder="http://localhost:5000/uploads/..." type="url" />
                            </div>
                            <div className="form-group">
                                <label>Year / Duration (optional)</label>
                                <input value={form.year} onChange={e => set('year', e.target.value)} placeholder="e.g. 2024-2026" />
                            </div>
                        </>)}
                    </div>
                    {error && <div className="modal-error">⚠ {error}</div>}
                    <div className="modal-actions">
                        <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
                        <button type="submit" className="btn-submit" disabled={saving || uploading}>
                            {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Add Person')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// ══════════════════════════════════════════════════════════════════════════════
//  ADD PROJECT MODAL
// ══════════════════════════════════════════════════════════════════════════════
function AddProjectModal({ defaultCategory, onClose, onSaved }) {
    const [form, setForm] = useState({
        category: defaultCategory || PROJECT_CATEGORIES[0],
        projectTitle: '', title: '', fundingAgency: '',
        projectDuration: '', fundingAmount: '', link: ''
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        const displayTitle = form.projectTitle.trim() || form.title.trim();
        if (!displayTitle) return setError('Project Title is required.');
        setSaving(true); setError('');
        try {
            const payload = {
                ...form,
                projectTitle: form.projectTitle || displayTitle,
                title: form.title || displayTitle
            };
            const res = await fetch(`${API}/projects`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!res.ok) throw new Error((await res.json()).error || 'Failed');
            onSaved();
        } catch (err) {
            setError(err.message);
        } finally { setSaving(false); }
    };

    return (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
            <div className="modal">
                <div className="modal-header">
                    <h2>Add Project</h2>
                    <button className="modal-close" onClick={onClose}>✕</button>
                </div>
                <form onSubmit={submit}>
                    <div className="form-grid">
                        <div className="form-group full-width">
                            <label>Category<span className="required-star">*</span></label>
                            <select value={form.category} onChange={e => set('category', e.target.value)}>
                                {PROJECT_CATEGORIES.map(c => <option key={c}>{c}</option>)}
                            </select>
                        </div>
                        <div className="form-group full-width">
                            <label>Project Title<span className="required-star">*</span></label>
                            <input value={form.projectTitle} onChange={e => set('projectTitle', e.target.value)} placeholder="Full project title" />
                        </div>
                        <div className="form-group full-width">
                            <label>Funding Agency</label>
                            <input value={form.fundingAgency} onChange={e => set('fundingAgency', e.target.value)} placeholder="e.g. DST, MeitY, Boeing" />
                        </div>
                        <div className="form-group">
                            <label>Project Duration</label>
                            <input value={form.projectDuration} onChange={e => set('projectDuration', e.target.value)} placeholder="e.g. 2024-2027" />
                        </div>
                        <div className="form-group">
                            <label>Funding Amount</label>
                            <input value={form.fundingAmount} onChange={e => set('fundingAmount', e.target.value)} placeholder="e.g. ₹ 25,00,000/-" />
                        </div>
                        <div className="form-group full-width">
                            <label>Link (optional)</label>
                            <input value={form.link} onChange={e => set('link', e.target.value)} placeholder="https://..." type="url" />
                        </div>
                    </div>
                    {error && <div className="modal-error">⚠ {error}</div>}
                    <div className="modal-actions">
                        <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
                        <button type="submit" className="btn-submit" disabled={saving}>
                            {saving ? 'Saving…' : 'Add Project'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// ══════════════════════════════════════════════════════════════════════════════
//  ADD PUBLICATION MODAL
// ══════════════════════════════════════════════════════════════════════════════
function AddPublicationModal({ defaultCategory, onClose, onSaved }) {
    const [form, setForm] = useState({
        category: defaultCategory || PUBLICATION_CATEGORIES[0],
        title: '', authors: '', bookTitle: '', publisher: '',
        journal: '', conference: '', year: '', volume: '',
        pages: '', impactFactor: '', indexing: '', doi: '',
        isbn: '', award: '', inventors: '', applicationNo: '',
        dateOfFiling: '', link: ''
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.title.trim()) return setError('Title is required.');
        setSaving(true); setError('');
        try {
            const payload = {
                ...form,
                year: form.year ? Number(form.year) : undefined,
                volume: form.volume ? Number(form.volume) : undefined,
                impactFactor: form.impactFactor ? Number(form.impactFactor) : undefined,
            };
            const res = await fetch(`${API}/publications`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!res.ok) throw new Error((await res.json()).error || 'Failed');
            onSaved();
        } catch (err) {
            setError(err.message);
        } finally { setSaving(false); }
    };

    const category = form.category;

    return (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
            <div className="modal">
                <div className="modal-header">
                    <h2>Add Publication / Patent</h2>
                    <button className="modal-close" onClick={onClose}>✕</button>
                </div>
                <form onSubmit={submit}>
                    <div className="form-grid">
                        <div className="form-group full-width">
                            <label>Category<span className="required-star">*</span></label>
                            <select value={form.category} onChange={e => set('category', e.target.value)}>
                                {PUBLICATION_CATEGORIES.map(c => <option key={c}>{c}</option>)}
                            </select>
                        </div>
                        <div className="form-group full-width">
                            <label>Title<span className="required-star">*</span></label>
                            <input value={form.title} onChange={e => set('title', e.target.value)} placeholder="Publication or patent title" />
                        </div>

                        {category === 'Book Chapters' && (
                            <>
                                <div className="form-group full-width">
                                    <label>Book Title</label>
                                    <input value={form.bookTitle} onChange={e => set('bookTitle', e.target.value)} placeholder="e.g. Information Security and Privacy" />
                                </div>
                                <div className="form-group">
                                    <label>Publisher</label>
                                    <input value={form.publisher} onChange={e => set('publisher', e.target.value)} placeholder="e.g. IntechOpen, CRC Press" />
                                </div>
                                <div className="form-group">
                                    <label>ISBN</label>
                                    <input value={form.isbn} onChange={e => set('isbn', e.target.value)} placeholder="ISBN number" />
                                </div>
                                <div className="form-group">
                                    <label>Year</label>
                                    <input type="number" value={form.year} onChange={e => set('year', e.target.value)} placeholder="e.g. 2023" />
                                </div>
                            </>
                        )}

                        {category === 'Journal Publications' && (
                            <>
                                <div className="form-group full-width">
                                    <label>Authors</label>
                                    <input value={form.authors} onChange={e => set('authors', e.target.value)} placeholder="Author names" />
                                </div>
                                <div className="form-group full-width">
                                    <label>Journal</label>
                                    <input value={form.journal} onChange={e => set('journal', e.target.value)} placeholder="e.g. IEEE Access" />
                                </div>
                                <div className="form-group">
                                    <label>Year</label>
                                    <input type="number" value={form.year} onChange={e => set('year', e.target.value)} placeholder="e.g. 2023" />
                                </div>
                                <div className="form-group">
                                    <label>Volume</label>
                                    <input type="number" value={form.volume} onChange={e => set('volume', e.target.value)} placeholder="e.g. 12" />
                                </div>
                                <div className="form-group">
                                    <label>Pages</label>
                                    <input value={form.pages} onChange={e => set('pages', e.target.value)} placeholder="e.g. 52727 – 52743" />
                                </div>
                                <div className="form-group">
                                    <label>Impact Factor</label>
                                    <input type="number" step="0.01" value={form.impactFactor} onChange={e => set('impactFactor', e.target.value)} placeholder="e.g. 3.9" />
                                </div>
                                <div className="form-group full-width">
                                    <label>Indexing</label>
                                    <input value={form.indexing} onChange={e => set('indexing', e.target.value)} placeholder="e.g. Scopus & SCIE (Q1)" />
                                </div>
                            </>
                        )}

                        {category === 'Conference Publications' && (
                            <>
                                <div className="form-group full-width">
                                    <label>Authors</label>
                                    <input value={form.authors} onChange={e => set('authors', e.target.value)} placeholder="Author names" />
                                </div>
                                <div className="form-group full-width">
                                    <label>Conference</label>
                                    <input value={form.conference} onChange={e => set('conference', e.target.value)} placeholder="Conference name" />
                                </div>
                                <div className="form-group">
                                    <label>Year</label>
                                    <input type="number" value={form.year} onChange={e => set('year', e.target.value)} placeholder="e.g. 2023" />
                                </div>
                                <div className="form-group">
                                    <label>Pages (optional)</label>
                                    <input value={form.pages} onChange={e => set('pages', e.target.value)} placeholder="e.g. 126-146" />
                                </div>
                                <div className="form-group">
                                    <label>Award (optional)</label>
                                    <input value={form.award} onChange={e => set('award', e.target.value)} placeholder="e.g. Best Paper Award" />
                                </div>
                                <div className="form-group">
                                    <label>DOI (optional)</label>
                                    <input value={form.doi} onChange={e => set('doi', e.target.value)} placeholder="e.g. 10.1109/..." />
                                </div>
                            </>
                        )}

                        {category === 'Patents' && (
                            <>
                                <div className="form-group full-width">
                                    <label>Inventors</label>
                                    <input value={form.inventors} onChange={e => set('inventors', e.target.value)} placeholder="Inventor names" />
                                </div>
                                <div className="form-group">
                                    <label>Application No.</label>
                                    <input value={form.applicationNo} onChange={e => set('applicationNo', e.target.value)} placeholder="Application number" />
                                </div>
                                <div className="form-group">
                                    <label>Date of Filing</label>
                                    <input value={form.dateOfFiling} onChange={e => set('dateOfFiling', e.target.value)} placeholder="e.g. 10/07/2024" />
                                </div>
                            </>
                        )}

                        <div className="form-group full-width">
                            <label>Link / URL (optional)</label>
                            <input type="url" value={form.link} onChange={e => set('link', e.target.value)} placeholder="https://..." />
                        </div>
                    </div>
                    {error && <div className="modal-error">⚠ {error}</div>}
                    <div className="modal-actions">
                        <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
                        <button type="submit" className="btn-submit" disabled={saving}>
                            {saving ? 'Saving…' : 'Add Publication'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// ══════════════════════════════════════════════════════════════════════════════
//  ADD SLIDESHOW MODAL
// ══════════════════════════════════════════════════════════════════════════════
function AddSlideshowModal({ onClose, onSaved }) {
    const [form, setForm] = useState({
        imageUrl: '', caption: '', order: 0
    });
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        setError('');
        const formData = new FormData();
        formData.append('image', file);

        try {
            const res = await fetch(`${API}/upload`, {
                method: 'POST',
                body: formData
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Upload failed');
            set('imageUrl', data.url);
        } catch (err) {
            setError('Failed to upload image: ' + err.message);
        } finally {
            setUploading(false);
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.imageUrl.trim()) return setError('Image URL/File is required.');
        setSaving(true); setError('');
        try {
            const res = await fetch(`${API}/slideshow`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(form)
            });
            if (!res.ok) throw new Error((await res.json()).error || 'Failed');
            onSaved();
        } catch (err) {
            setError(err.message);
        } finally { setSaving(false); }
    };

    return (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
            <div className="modal">
                <div className="modal-header">
                    <h2>Add Slideshow Image</h2>
                    <button className="modal-close" onClick={onClose}>✕</button>
                </div>
                <form onSubmit={submit}>
                    <div className="form-grid">
                        <div className="form-group full-width">
                            <label>Upload Slide Image</label>
                            <input type="file" accept="image/*" onChange={handleFileUpload} />
                            {uploading && <span className="upload-loader">Uploading...</span>}
                        </div>
                        <div className="form-group full-width">
                            <label>Image URL (or enter manually)<span className="required-star">*</span></label>
                            <input value={form.imageUrl} onChange={e => set('imageUrl', e.target.value)}
                                placeholder="http://localhost:5000/uploads/..." type="url" />
                        </div>
                        <div className="form-group full-width">
                            <label>Caption</label>
                            <input value={form.caption} onChange={e => set('caption', e.target.value)} placeholder="Image caption" />
                        </div>
                        <div className="form-group">
                            <label>Display Order</label>
                            <input type="number" value={form.order} onChange={e => set('order', parseInt(e.target.value) || 0)} />
                        </div>
                    </div>
                    {error && <div className="modal-error">⚠ {error}</div>}
                    <div className="modal-actions">
                        <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
                        <button type="submit" className="btn-submit" disabled={saving || uploading}>
                            {saving ? 'Saving…' : 'Add Slide'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// ══════════════════════════════════════════════════════════════════════════════
//  MAIN ADMIN PAGE COMPONENT
// ══════════════════════════════════════════════════════════════════════════════
export default function AdminPage() {
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [username, setUsername] = useState('');
    const [loginError, setLoginError] = useState('');
    const [activeTab, setActiveTab] = useState('updates');

    // Password refs — never exposed in DOM
    const passwordRef = useRef(null);
    const cpCurrentRef = useRef(null);
    const cpNewRef = useRef(null);
    const cpConfirmRef = useRef(null);
    const cuPasswordRef = useRef(null);
    const cuNewUsernameRef = useRef(null);

    // 2FA OTP state
    const [requireOTP, setRequireOTP] = useState(false);
    const [otpToken, setOtpToken] = useState('');
    const [otpEmail, setOtpEmail] = useState('');

    // DB state
    const [updates, setUpdates] = useState([]);
    const [announcements, setAnnouncements] = useState([]);
    const [people, setPeople] = useState({});
    const [projects, setProjects] = useState({});
    const [publications, setPublications] = useState({});
    const [slideshow, setSlideshow] = useState([]);

    // Alerts/Forms state
    const [newUpdate, setNewUpdate] = useState({ text: '', link: '' });
    const [newAnnouncement, setNewAnnouncement] = useState({ text: '', link: '' });
    const [showPersonModal, setShowPersonModal] = useState(false);
    const [editingPerson, setEditingPerson] = useState(null);
    const [showProjectModal, setShowProjectModal] = useState(false);
    const [showPublicationModal, setShowPublicationModal] = useState(false);
    const [showSlideshowModal, setShowSlideshowModal] = useState(false);
    const [activeCategory, setActiveCategory] = useState('');

    const [errorMsg, setErrorMsg] = useState('');
    const [infoMsg, setInfoMsg] = useState('');
    const [confirmAction, setConfirmAction] = useState(null);

    // Change password state
    const [cpError, setCpError] = useState('');
    const [cpSuccess, setCpSuccess] = useState('');

    // Change username state
    const [cuError, setCuError] = useState('');
    const [cuSuccess, setCuSuccess] = useState('');

    const checkLogin = useCallback(() => {
        const stored = localStorage.getItem('csrl_admin_user');
        if (stored) {
            setIsLoggedIn(true);
        }
    }, []);

    useEffect(() => {
        checkLogin();
    }, [checkLogin]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoginError('');
        const pwd = passwordRef.current?.value || '';
        try {
            const res = await fetch(`${API}/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password: pwd })
            });
            const data = await res.json();
            if (res.ok && data.success) {
                if (data.requireOTP) {
                    setRequireOTP(true);
                    setOtpEmail(data.email);
                } else {
                    localStorage.setItem('csrl_admin_user', username);
                    setIsLoggedIn(true);
                }
            } else {
                setLoginError(data.message || 'Invalid credentials');
            }
        } catch (err) {
            setLoginError('Server connection failed. Make sure server is running on port 5000.');
        }
    };

    const handleVerifyOTP = async (e) => {
        e.preventDefault();
        setLoginError('');
        try {
            const res = await fetch(`${API}/verify-otp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, token: otpToken })
            });
            const data = await res.json();
            if (res.ok && data.success) {
                localStorage.setItem('csrl_admin_user', username);
                setIsLoggedIn(true);
                setRequireOTP(false);
                setOtpToken('');
            } else {
                setLoginError(data.message || 'Invalid verification code');
            }
        } catch (err) {
            setLoginError('Verification failed. Server connection error.');
        }
    };

    const maskEmail = (email) => {
        if (!email) return 'your email';
        const [local, domain] = email.split('@');
        if (local.length <= 2) return `${local}***@${domain}`;
        return `${local.substring(0, 2)}***${local.substring(local.length - 1)}@${domain}`;
    };

    const handleLogout = () => {
        localStorage.removeItem('csrl_admin_user');
        setIsLoggedIn(false);
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        setCpError('');
        setCpSuccess('');
        const currentPassword = cpCurrentRef.current?.value || '';
        const newPassword = cpNewRef.current?.value || '';
        const confirmPassword = cpConfirmRef.current?.value || '';
        if (newPassword !== confirmPassword) {
            setCpError('New passwords do not match');
            return;
        }
        if (newPassword.length < 6) {
            setCpError('New password must be at least 6 characters');
            return;
        }
        try {
            const storedUser = localStorage.getItem('csrl_admin_user') || username;
            const res = await fetch(`${API}/change-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username: storedUser, currentPassword, newPassword })
            });
            const data = await res.json();
            if (res.ok && data.success) {
                setCpSuccess('Password changed successfully! Please log in again.');
                if (cpCurrentRef.current) cpCurrentRef.current.value = '';
                if (cpNewRef.current) cpNewRef.current.value = '';
                if (cpConfirmRef.current) cpConfirmRef.current.value = '';
                setTimeout(() => { handleLogout(); }, 2000);
            } else {
                setCpError(data.message || 'Failed to change password');
            }
        } catch (err) {
            setCpError('Server connection failed.');
        }
    };

    const handleChangeUsername = async (e) => {
        e.preventDefault();
        setCuError('');
        setCuSuccess('');
        const newUsername = cuNewUsernameRef.current?.value?.trim() || '';
        const pwd = cuPasswordRef.current?.value || '';
        if (newUsername.length < 3) {
            setCuError('Username must be at least 3 characters');
            return;
        }
        try {
            const storedUser = localStorage.getItem('csrl_admin_user') || username;
            const res = await fetch(`${API}/change-username`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ currentUsername: storedUser, currentPassword: pwd, newUsername })
            });
            const data = await res.json();
            if (res.ok && data.success) {
                setCuSuccess('Username changed! Please log in again with your new username.');
                if (cuNewUsernameRef.current) cuNewUsernameRef.current.value = '';
                if (cuPasswordRef.current) cuPasswordRef.current.value = '';
                setTimeout(() => { handleLogout(); }, 2000);
            } else {
                setCuError(data.message || 'Failed to change username');
            }
        } catch (err) {
            setCuError('Server connection failed.');
        }
    };

    const fetchAll = useCallback(async () => {
        if (!isLoggedIn) return;
        try {
            // Fetch updates
            let res = await fetch(`${API}/updates`);
            setUpdates(await res.json());

            // Fetch announcements
            res = await fetch(`${API}/announcements`);
            setAnnouncements(await res.json());

            // Fetch people
            res = await fetch(`${API}/admin/people`);
            setPeople(await res.json());

            // Fetch projects
            res = await fetch(`${API}/admin/projects`);
            setProjects(await res.json());

            // Fetch slideshow images
            res = await fetch(`${API}/slideshow`);
            setSlideshow(await res.json());

            // Fetch publications
            res = await fetch(`${API}/admin/publications`);
            setPublications(await res.json());

        } catch (err) {
            setErrorMsg('Failed to sync content from backend database.');
        }
    }, [isLoggedIn]);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    const triggerInfo = (msg) => {
        setInfoMsg(msg);
        setTimeout(() => setInfoMsg(''), 3000);
    };

    // ─── Post Update ──────────────────────────────────────────────────────────
    const addUpdate = async (e) => {
        e.preventDefault();
        if (!newUpdate.text.trim()) return;
        try {
            const res = await fetch(`${API}/updates`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newUpdate)
            });
            if (!res.ok) throw new Error();
            setNewUpdate({ text: '', link: '' });
            fetchAll();
            triggerInfo('Update banner added!');
        } catch (err) {
            setErrorMsg('Failed to add update banner.');
        }
    };

    const deleteUpdate = (id) => {
        setConfirmAction({
            message: 'Are you sure you want to delete this update banner?',
            onConfirm: async () => {
                setConfirmAction(null);
                try {
                    await fetch(`${API}/updates/${id}`, { method: 'DELETE' });
                    fetchAll();
                    triggerInfo('Update deleted');
                } catch (err) {
                    setErrorMsg('Failed to delete.');
                }
            }
        });
    };

    // ─── Post Announcement ────────────────────────────────────────────────────
    const addAnnouncement = async (e) => {
        e.preventDefault();
        if (!newAnnouncement.text.trim()) return;
        try {
            const res = await fetch(`${API}/announcements`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newAnnouncement)
            });
            if (!res.ok) throw new Error();
            setNewAnnouncement({ text: '', link: '' });
            fetchAll();
            triggerInfo('Announcement added!');
        } catch (err) {
            setErrorMsg('Failed to add announcement.');
        }
    };

    const deleteAnnouncement = (id) => {
        setConfirmAction({
            message: 'Are you sure you want to delete this announcement?',
            onConfirm: async () => {
                setConfirmAction(null);
                try {
                    await fetch(`${API}/announcements/${id}`, { method: 'DELETE' });
                    fetchAll();
                    triggerInfo('Announcement deleted');
                } catch (err) {
                    setErrorMsg('Failed to delete.');
                }
            }
        });
    };

    // ─── Delete Person ────────────────────────────────────────────────────────
    const deletePerson = (id) => {
        setConfirmAction({
            message: 'Are you sure you want to remove this person from the contributors list?',
            onConfirm: async () => {
                setConfirmAction(null);
                try {
                    const res = await fetch(`${API}/people/${id}`, { method: 'DELETE' });
                    if (!res.ok) throw new Error();
                    fetchAll();
                    triggerInfo('Person removed successfully');
                } catch (err) {
                    setErrorMsg('Failed to remove person.');
                }
            }
        });
    };

    // ─── Move Person to Alumni ──────────────────────────────────────────────────
    const moveToAlumni = (id) => {
        setConfirmAction({
            message: 'Are you sure you want to move this contributor to the Alumni section?',
            onConfirm: async () => {
                setConfirmAction(null);
                try {
                    let res = await fetch(`${API}/people/${id}`, {
                        method: 'PATCH',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ category: 'Alumni' })
                    });
                    if (!res.ok) {
                        res = await fetch(`${API}/people/${id}`, {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ category: 'Alumni' })
                        });
                    }
                    if (!res.ok) throw new Error('Server returned an error status.');
                    await fetchAll();
                    triggerInfo('Moved to Alumni category successfully');
                } catch (err) {
                    console.error('Error moving to Alumni:', err);
                    setErrorMsg('Failed to update category: ' + (err.message || 'Unknown error'));
                }
            }
        });
    };

    // ─── Move Person to Past Contributors ───────────────────────────────────────
    const moveToPastContributors = (id) => {
        setConfirmAction({
            message: 'Are you sure you want to move this intern to the Past Contributors section?',
            onConfirm: async () => {
                setConfirmAction(null);
                try {
                    let res = await fetch(`${API}/people/${id}`, {
                        method: 'PATCH',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ category: 'Past Contributors' })
                    });
                    if (!res.ok) {
                        res = await fetch(`${API}/people/${id}`, {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ category: 'Past Contributors' })
                        });
                    }
                    if (!res.ok) throw new Error('Server returned an error status.');
                    await fetchAll();
                    triggerInfo('Moved to Past Contributors successfully');
                } catch (err) {
                    console.error('Error moving to Past Contributors:', err);
                    setErrorMsg('Failed to update category: ' + (err.message || 'Unknown error'));
                }
            }
        });
    };

    // ─── Delete Project ───────────────────────────────────────────────────────
    const deleteProject = (id) => {
        setConfirmAction({
            message: 'Are you sure you want to delete this research project?',
            onConfirm: async () => {
                setConfirmAction(null);
                try {
                    const res = await fetch(`${API}/projects/${id}`, { method: 'DELETE' });
                    if (!res.ok) throw new Error();
                    fetchAll();
                    triggerInfo('Project deleted');
                } catch (err) {
                    setErrorMsg('Failed to delete project.');
                }
            }
        });
    };

    // ─── Slideshow Images Delete ──────────────────────────────────────────────
    const deleteSlide = (id) => {
        setConfirmAction({
            message: 'Are you sure you want to remove this slide image from the homepage?',
            onConfirm: async () => {
                setConfirmAction(null);
                try {
                    const res = await fetch(`${API}/slideshow/${id}`, { method: 'DELETE' });
                    if (!res.ok) throw new Error();
                    fetchAll();
                    triggerInfo('Slide removed');
                } catch (err) {
                    setErrorMsg('Failed to delete slide.');
                }
            }
        });
    };

    // ─── Publications & Patents Delete ──────────────────────────────────────────
    const deletePublication = (id) => {
        setConfirmAction({
            message: 'Are you sure you want to delete this publication/patent entry?',
            onConfirm: async () => {
                setConfirmAction(null);
                try {
                    const res = await fetch(`${API}/publications/${id}`, { method: 'DELETE' });
                    if (!res.ok) throw new Error();
                    fetchAll();
                    triggerInfo('Publication deleted');
                } catch (err) {
                    setErrorMsg('Failed to delete publication.');
                }
            }
        });
    };

    const updateSlideOrder = async (id, currentOrder, delta) => {
        try {
            const res = await fetch(`${API}/slideshow/${id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ order: currentOrder + delta })
            });
            if (!res.ok) throw new Error();
            fetchAll();
        } catch (err) {
            setErrorMsg('Failed to reorder slide.');
        }
    };

    // Login Form UI
    if (!isLoggedIn) {
        return (
            <div className="login-wrapper">
                <div className="login-card">
                    <h2>CSRL Admin Login</h2>
                    
                    {requireOTP ? (
                        <form onSubmit={handleVerifyOTP}>
                            <p style={{ color: '#94a3b8', fontSize: '14px', marginBottom: '20px', lineHeight: '1.5' }}>
                                A 2-Factor verification code has been sent to <strong>{maskEmail(otpEmail)}</strong>. Please enter the 6-digit code below:
                            </p>
                            <div className="form-group">
                                <label>Verification Code</label>
                                <input value={otpToken} onChange={e => setOtpToken(e.target.value)} required placeholder="123456" maxLength={6} pattern="\d{6}" style={{ letterSpacing: '8px', textAlign: 'center', fontSize: '20px' }} />
                            </div>
                            {loginError && <div className="login-error">⚠ {loginError}</div>}
                            <button type="submit" className="login-btn">Verify Code</button>
                            <button type="button" className="login-btn btn-secondary" style={{ marginTop: '10px', background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', color: '#94a3b8', boxShadow: 'none' }} onClick={() => { setRequireOTP(false); setLoginError(''); }}>
                                ← Back to Login
                            </button>
                        </form>
                    ) : (
                        <form onSubmit={handleLogin}>
                            <div className="form-group">
                                <label>Username</label>
                                <input value={username} onChange={e => setUsername(e.target.value)} required placeholder="admin" />
                            </div>
                            <div className="form-group">
                                <label>Password</label>
                                <input type="password" ref={passwordRef} autoComplete="current-password" required placeholder="••••••••" />
                            </div>
                            {loginError && <div className="login-error">⚠ {loginError}</div>}
                            <button type="submit" className="login-btn">Login</button>
                        </form>
                    )}

                    <div className="login-footer">
                        <Link to="/">← Back to CSRL Homepage</Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-wrapper">
            <header className="admin-header">
                <div className="header-left">
                    <h1>CSRL Control Dashboard</h1>
                    <span className="database-badge">✓ MongoDB Connected</span>
                </div>
                <div className="header-right">
                    <Link className="btn-secondary" to="/" target="_blank">View Live Site</Link>
                    <button className="btn-logout" onClick={handleLogout}>Log Out</button>
                </div>
            </header>

            {infoMsg && <div className="toast-info">✓ {infoMsg}</div>}
            {errorMsg && <div className="toast-error" onClick={() => setErrorMsg('')}>⚠ {errorMsg} (Click to dismiss)</div>}

            <div className="admin-layout">
                <aside className="admin-sidebar">
                    <nav className="nav-tabs">
                        <button className={`tab-link ${activeTab === 'updates' ? 'active' : ''}`} onClick={() => setActiveTab('updates')}>
                            📣 Updates & Announcements
                        </button>
                        <button className={`tab-link ${activeTab === 'people' ? 'active' : ''}`} onClick={() => setActiveTab('people')}>
                            👥 Lab Contributors
                        </button>
                        <button className={`tab-link ${activeTab === 'projects' ? 'active' : ''}`} onClick={() => setActiveTab('projects')}>
                            📁 Research Projects
                        </button>
                        <button className={`tab-link ${activeTab === 'publications' ? 'active' : ''}`} onClick={() => setActiveTab('publications')}>
                            📚 Publications & Patents
                        </button>
                        <button className={`tab-link ${activeTab === 'slideshow' ? 'active' : ''}`} onClick={() => setActiveTab('slideshow')}>
                            🖼 Homepage Slideshow
                        </button>
                        <button className={`tab-link ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => setActiveTab('settings')}>
                            ⚙ Settings
                        </button>
                    </nav>
                </aside>

                <main className="admin-main">
                    {/* ════════════════ TAB: UPDATES ════════════════ */}
                    {activeTab === 'updates' && (
                        <div className="tab-pane">
                            <div className="pane-section">
                                <h2>Manage Important Update Banner</h2>
                                <form onSubmit={addUpdate} className="inline-form">
                                    <input value={newUpdate.text} onChange={e => setNewUpdate({ ...newUpdate, text: e.target.value })}
                                        placeholder="Banner text alert" required className="flex-grow" />
                                    <input value={newUpdate.link} onChange={e => setNewUpdate({ ...newUpdate, link: e.target.value })}
                                        placeholder="Target URL (Optional)" type="url" />
                                    <button type="submit" className="btn-primary">Add Banner Update</button>
                                </form>
                                <div className="list-cards">
                                    {updates.map(item => (
                                        <div key={item._id} className="item-card">
                                            <div className="card-details">
                                                <p className="card-text">{item.text}</p>
                                                {item.link && <a href={item.link} className="card-link" target="_blank" rel="noreferrer">{item.link}</a>}
                                            </div>
                                            <button className="btn-danger" onClick={() => deleteUpdate(item._id)}>Delete</button>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <hr className="divider" />

                            <div className="pane-section">
                                <h2>Manage Homepage Announcements</h2>
                                <form onSubmit={addAnnouncement} className="inline-form">
                                    <input value={newAnnouncement.text} onChange={e => setNewAnnouncement({ ...newAnnouncement, text: e.target.value })}
                                        placeholder="Announcement text details" required className="flex-grow" />
                                    <input value={newAnnouncement.link} onChange={e => setNewAnnouncement({ ...newAnnouncement, link: e.target.value })}
                                        placeholder="Details Link (Optional)" type="url" />
                                    <button type="submit" className="btn-primary">Add Announcement</button>
                                </form>
                                <div className="list-cards">
                                    {announcements.map(item => (
                                        <div key={item._id} className="item-card">
                                            <div className="card-details">
                                                <p className="card-text">{item.text}</p>
                                                {item.link && <a href={item.link} className="card-link" target="_blank" rel="noreferrer">{item.link}</a>}
                                            </div>
                                            <button className="btn-danger" onClick={() => deleteAnnouncement(item._id)}>Delete</button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ════════════════ TAB: PEOPLE ════════════════ */}
                    {activeTab === 'people' && (
                        <div className="tab-pane">
                            <h2>Manage Lab Contributors</h2>
                            {PEOPLE_CATEGORIES.map(category => (
                                <section key={category} className="category-section">
                                    <div className="section-title-row">
                                        <h3>{category}</h3>
                                        {category !== 'Professor In-charge' && (
                                            <button className="btn-secondary btn-sm" onClick={() => { setActiveCategory(category); setShowPersonModal(true); }}>
                                                + Add Member
                                            </button>
                                        )}
                                    </div>
                                    <div className="entity-grid">
                                        {(people[category] || []).map(person => (
                                            <div key={person._id} className="entity-card">
                                                {person.image && <img src={person.image} alt={person.name} className="entity-img-preview" />}
                                                <div className="entity-info">
                                                    <h4>{person.name}</h4>
                                                    {person.designation && <p className="designation">{person.designation}</p>}
                                                    {person.researchInterests && <p className="interests"><strong>Interests:</strong> {person.researchInterests}</p>}
                                                </div>
                                                <div style={{ display: 'flex', gap: '8px', marginTop: '14px', flexWrap: 'wrap' }}>
                                                    <button className="btn-secondary btn-sm" onClick={() => setEditingPerson(person)}>Edit</button>
                                                    {CAN_MOVE_TO_ALUMNI.includes(category) && (
                                                        <button className="btn-secondary btn-sm" style={{ backgroundColor: '#1e3a8a', color: '#60a5fa' }} onClick={() => moveToAlumni(person._id)}>Move to Alumni</button>
                                                    )}
                                                    {category === 'Interns' && (
                                                        <button className="btn-secondary btn-sm" style={{ backgroundColor: '#1e3a8a', color: '#60a5fa' }} onClick={() => moveToPastContributors(person._id)}>Move to Past Contributors</button>
                                                    )}
                                                    {category !== 'Professor In-charge' && (
                                                        <button className="btn-danger btn-sm" style={{ margin: 0 }} onClick={() => deletePerson(person._id)}>Remove</button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                        {(!people[category] || people[category].length === 0) && <p className="empty-indicator">No members listed in this category.</p>}
                                    </div>
                                </section>
                            ))}
                        </div>
                    )}

                    {/* ════════════════ TAB: PROJECTS ════════════════ */}
                    {activeTab === 'projects' && (
                        <div className="tab-pane">
                            <h2>Manage Research Projects</h2>
                            {PROJECT_CATEGORIES.map(category => (
                                <section key={category} className="category-section">
                                    <div className="section-title-row">
                                        <h3>{category}</h3>
                                        <button className="btn-secondary btn-sm" onClick={() => { setActiveCategory(category); setShowProjectModal(true); }}>
                                            + Add Project
                                        </button>
                                    </div>
                                    <div className="list-cards">
                                        {(projects[category] || []).map(project => (
                                            <div key={project._id} className="item-card">
                                                <div className="card-details">
                                                    <h4>{project.projectTitle || project.title}</h4>
                                                    {project.fundingAgency && <p><strong>Funding:</strong> {project.fundingAgency} {project.fundingAmount && `(${project.fundingAmount})`}</p>}
                                                    {project.projectDuration && <p><strong>Duration:</strong> {project.projectDuration}</p>}
                                                    {project.link && <a href={project.link} target="_blank" rel="noreferrer" className="card-link">{project.link}</a>}
                                                </div>
                                                <button className="btn-danger" onClick={() => deleteProject(project._id)}>Delete</button>
                                            </div>
                                        ))}
                                        {(!projects[category] || projects[category].length === 0) && <p className="empty-indicator">No projects listed in this category.</p>}
                                    </div>
                                </section>
                            ))}
                        </div>
                    )}

                    {/* ════════════════ TAB: PUBLICATIONS ════════════════ */}
                    {activeTab === 'publications' && (
                        <div className="tab-pane">
                            <h2>Manage Publications & Patents</h2>
                            {PUBLICATION_CATEGORIES.map(category => (
                                <section key={category} className="category-section">
                                    <div className="section-title-row">
                                        <h3>{category}</h3>
                                        <button className="btn-secondary btn-sm" onClick={() => { setActiveCategory(category); setShowPublicationModal(true); }}>
                                            + Add Publication
                                        </button>
                                    </div>
                                    <div className="list-cards">
                                        {(publications[category] || []).map(pub => (
                                            <div key={pub._id} className="item-card">
                                                <div className="card-details">
                                                    <h4>{pub.title}</h4>
                                                    {pub.authors && <p><strong>Authors:</strong> {pub.authors}</p>}
                                                    {pub.bookTitle && <p><strong>Book:</strong> {pub.bookTitle}</p>}
                                                    {pub.publisher && <p><strong>Publisher:</strong> {pub.publisher}</p>}
                                                    {pub.journal && <p><strong>Journal:</strong> {pub.journal} {pub.year && `, ${pub.year}`}</p>}
                                                    {pub.conference && <p><strong>Conference:</strong> {pub.conference} {pub.year && `, ${pub.year}`}</p>}
                                                    {pub.volume && <p><strong>Volume:</strong> {pub.volume} {pub.pages && `, Pages: ${pub.pages}`}</p>}
                                                    {pub.impactFactor && <p><strong>Impact Factor:</strong> {pub.impactFactor}</p>}
                                                    {pub.indexing && <p><strong>Indexing:</strong> {pub.indexing}</p>}
                                                    {pub.doi && <p><strong>DOI:</strong> {pub.doi}</p>}
                                                    {pub.isbn && <p><strong>ISBN:</strong> {pub.isbn}</p>}
                                                    {pub.award && <p><strong>Award:</strong> {pub.award}</p>}
                                                    {pub.inventors && <p><strong>Inventors:</strong> {pub.inventors}</p>}
                                                    {pub.applicationNo && <p><strong>Application No:</strong> {pub.applicationNo}</p>}
                                                    {pub.dateOfFiling && <p><strong>Date of Filing:</strong> {pub.dateOfFiling}</p>}
                                                    {pub.link && <a href={pub.link} target="_blank" rel="noreferrer" className="card-link">{pub.link}</a>}
                                                </div>
                                                <button className="btn-danger" onClick={() => deletePublication(pub._id)}>Delete</button>
                                            </div>
                                        ))}
                                        {(!publications[category] || publications[category].length === 0) && <p className="empty-indicator">No publications listed in this category.</p>}
                                    </div>
                                </section>
                            ))}
                        </div>
                    )}

                    {/* ════════════════ TAB: SLIDESHOW ════════════════ */}
                    {activeTab === 'slideshow' && (
                        <div className="tab-pane">
                            <div className="section-title-row">
                                <h2>Manage Homepage Slideshow Photos</h2>
                                <button className="btn-primary" onClick={() => setShowSlideshowModal(true)}>
                                    + Add New Slide
                                </button>
                            </div>
                            <div className="entity-grid">
                                {slideshow.map((slide, index) => (
                                    <div key={slide._id} className="entity-card slide-card">
                                        <img src={slide.imageUrl} alt={slide.caption || 'Slide'} className="entity-img-preview slide-preview" />
                                        <div className="entity-info">
                                            <h4>{slide.caption || '(No caption)'}</h4>
                                            <p className="order-tag">Order: <strong>{slide.order}</strong></p>
                                        </div>
                                        <div className="slide-actions">
                                            <button className="btn-secondary btn-xs" disabled={index === 0} onClick={() => updateSlideOrder(slide._id, slide.order, -1)}>▲ Up</button>
                                            <button className="btn-secondary btn-xs" disabled={index === slideshow.length - 1} onClick={() => updateSlideOrder(slide._id, slide.order, 1)}>▼ Down</button>
                                            <button className="btn-danger btn-sm" onClick={() => deleteSlide(slide._id)}>Remove</button>
                                        </div>
                                    </div>
                                ))}
                                {slideshow.length === 0 && <p className="empty-indicator">No slideshow images loaded.</p>}
                            </div>
                        </div>
                    )}

                    {/* ════════════════ TAB: SETTINGS ════════════════ */}
                    {activeTab === 'settings' && (
                        <div className="tab-pane">
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: '28px', alignItems: 'start' }}>

                                {/* ── Change Username Card ── */}
                                <div className="pane-section" style={{ margin: 0 }}>
                                    <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <span style={{ fontSize: '20px' }}>👤</span> Change Username
                                    </h2>
                                    <p style={{ color: '#94a3b8', fontSize: '14px', marginBottom: '20px', lineHeight: '1.6' }}>
                                        Enter your current password to confirm identity, then set a new admin username.
                                        You will be logged out after the change.
                                    </p>
                                    <form onSubmit={handleChangeUsername} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                                        <div className="form-group">
                                            <label>Current Username</label>
                                            <input
                                                type="text"
                                                value={localStorage.getItem('csrl_admin_user') || username}
                                                readOnly
                                                style={{ opacity: 0.5, cursor: 'not-allowed' }}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>New Username</label>
                                            <input
                                                type="text"
                                                ref={cuNewUsernameRef}
                                                required
                                                minLength={3}
                                                placeholder="At least 3 characters"
                                                autoComplete="username"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Current Password (to confirm)</label>
                                            <input
                                                type="password"
                                                ref={cuPasswordRef}
                                                required
                                                placeholder="Enter your current password"
                                                autoComplete="current-password"
                                            />
                                        </div>
                                        {cuError && <div className="login-error" style={{ margin: 0 }}>⚠ {cuError}</div>}
                                        {cuSuccess && <div style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.3)', color: '#6ee7b7', padding: '10px 14px', borderRadius: '8px', fontSize: '14px' }}>✓ {cuSuccess}</div>}
                                        <button type="submit" className="btn-primary" style={{ alignSelf: 'flex-start', padding: '10px 24px' }}>Change Username</button>
                                    </form>
                                </div>

                                {/* ── Change Password Card ── */}
                                <div className="pane-section" style={{ margin: 0 }}>
                                    <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <span style={{ fontSize: '20px' }}>🔑</span> Change Password
                                    </h2>
                                    <p style={{ color: '#94a3b8', fontSize: '14px', marginBottom: '20px', lineHeight: '1.6' }}>
                                        Update your admin password. Minimum 6 characters required.
                                        You will be logged out after the change.
                                    </p>
                                    <form onSubmit={handleChangePassword} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                                        <div className="form-group">
                                            <label>Current Password</label>
                                            <input type="password" ref={cpCurrentRef} autoComplete="current-password" required placeholder="Enter current password" />
                                        </div>
                                        <div className="form-group">
                                            <label>New Password</label>
                                            <input type="password" ref={cpNewRef} autoComplete="new-password" required placeholder="At least 6 characters" />
                                        </div>
                                        <div className="form-group">
                                            <label>Confirm New Password</label>
                                            <input type="password" ref={cpConfirmRef} autoComplete="new-password" required placeholder="Repeat new password" />
                                        </div>
                                        {cpError && <div className="login-error" style={{ margin: 0 }}>⚠ {cpError}</div>}
                                        {cpSuccess && <div style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.3)', color: '#6ee7b7', padding: '10px 14px', borderRadius: '8px', fontSize: '14px' }}>✓ {cpSuccess}</div>}
                                        <button type="submit" className="btn-primary" style={{ alignSelf: 'flex-start', padding: '10px 24px' }}>Change Password</button>
                                    </form>
                                </div>

                            </div>
                        </div>
                    )}
                </main>
            </div>

            {/* Modals */}
            {showPersonModal && (
                <PersonModal defaultCategory={activeCategory} onClose={() => setShowPersonModal(false)}
                    onSaved={() => { setShowPersonModal(false); fetchAll(); triggerInfo('Contributor added'); }} />
            )}

            {editingPerson && (
                <PersonModal personToEdit={editingPerson} onClose={() => setEditingPerson(null)}
                    onSaved={() => { setEditingPerson(null); fetchAll(); triggerInfo('Contributor updated'); }} />
            )}

            {showProjectModal && (
                <AddProjectModal defaultCategory={activeCategory} onClose={() => setShowProjectModal(false)}
                    onSaved={() => { setShowProjectModal(false); fetchAll(); triggerInfo('Project added'); }} />
            )}

            {showSlideshowModal && (
                <AddSlideshowModal onClose={() => setShowSlideshowModal(false)}
                    onSaved={() => { setShowSlideshowModal(false); fetchAll(); triggerInfo('Slide image added'); }} />
            )}

            {showPublicationModal && (
                <AddPublicationModal defaultCategory={activeCategory} onClose={() => setShowPublicationModal(false)}
                    onSaved={() => { setShowPublicationModal(false); fetchAll(); triggerInfo('Publication added'); }} />
            )}

            {confirmAction && (
                <div className="modal-overlay" style={{ zIndex: 1000 }} onClick={() => setConfirmAction(null)}>
                    <div className="modal" style={{ maxWidth: '400px', width: '90%', padding: '24px' }}>
                        <div className="modal-header" style={{ marginBottom: '14px' }}>
                            <h2 style={{ fontSize: '20px', margin: 0 }}>Confirm Action</h2>
                        </div>
                        <div style={{ marginBottom: '24px', color: '#cbd5e1', fontSize: '15px', lineHeight: '1.5' }}>
                            {confirmAction.message}
                        </div>
                        <div className="modal-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                            <button type="button" className="btn-cancel" style={{ margin: 0 }} onClick={() => setConfirmAction(null)}>Cancel</button>
                            <button type="button" className="btn-submit" style={{ margin: 0, backgroundColor: '#dc2626', borderColor: '#dc2626' }} onClick={confirmAction.onConfirm}>Confirm</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
